/** @file toApiError: a non-2xx reply (status and parsed body) as the Pose3DError it reports. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Pose3DError } from '@zilo/pose3d-std/errors/pose3DError';
import type { ErrorBody } from '../types/restAdded';
import { HTTP_STATUS_CODES } from './httpStatusCodes';
import { makeError } from './makeError';

type Envelope = ErrorBody['error'];

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function envelopeOf(body: unknown): Envelope | null {
  if (!isRecord(body) || !isRecord(body.error)) return null;
  const e = body.error;
  if (typeof e.code !== 'string') return null;
  return {
    code: e.code, message: typeof e.message === 'string' ? e.message : e.code, params: isRecord(e.params) ? e.params : {},
    ...(typeof e.path === 'string' ? { path: e.path } : {}), ...(typeof e.hint === 'string' ? { hint: e.hint } : {}),
  };
}

/** `ErrorBody` → the same code, params, path and hint; any other body → the status table's code (else `TRANSPORT`) with `params.status`. */
export function toApiError(status: number, body: unknown): Pose3DError & Error {
  const envelope = envelopeOf(body);
  if (envelope !== null) {
    const ctx = { ...(envelope.path === undefined ? {} : { path: envelope.path }), ...(envelope.hint === undefined ? {} : { hint: envelope.hint }) };
    return makeError(envelope.code, envelope.params, ctx);
  }
  return makeError(HTTP_STATUS_CODES[status] ?? ERROR_CODES.TRANSPORT, { status }, { hint: `HTTP ${String(status)} without an error body` });
}
